import { Link, Route, Routes } from "react-router-dom";

import type { PortalApi } from "./api/portal";
import { JobDetail } from "./pages/JobDetail";
import { Overview } from "./pages/Overview";
import { useOverview } from "./useOverview";

interface Props {
  api: PortalApi;
}

/**
 * The fleet dashboard — which machines are backing up, which jobs have
 * quietly stopped. Lives under `/dashboard`, inside the same header and
 * footer as the rest of the site (see `marketing/MarketingLayout.tsx`); only
 * the content between them is specific to this page.
 *
 * `api.isDemo` alone decides whether the banner shows and offers to create an
 * account — the route wiring in `App.tsx` only ever pairs the demo api with
 * the public, signed-out `/demo` route (a signed-in visitor there is
 * redirected to their own dashboard before this ever renders), so there is no
 * case where the data is synthetic but the visitor already has an account.
 */
export function Dashboard({ api }: Props) {
  const { data, error, loading, reload } = useOverview(api);

  // Reload after a successful removal rather than filtering `data` locally:
  // the server (or, on `/demo`, the synthetic source) is the one place that
  // actually knows what's left, and re-asking it is what keeps this in step
  // with the same self-correction a live machine's own next report gets.
  async function removeMachine(machineId: string) {
    await api.removeMachine(machineId);
    reload();
  }

  async function clearJobHistory(machineId: string, jobId: string) {
    await api.clearJobHistory(machineId, jobId);
    reload();
  }

  return (
    <>
      {api.isDemo && (
        <p className="demo-banner" role="status">
          <strong>Demo data.</strong> Everything below is synthetic — an example
          of what the dashboard looks like once machines are reporting. Nothing
          here reflects a real vault.{" "}
          <Link to="/register">Create an account</Link> to see your own.
        </p>
      )}

      {loading && <p className="muted">Loading…</p>}

      {error && (
        <div className="empty empty--error" role="alert">
          <h2>Could not load your backups</h2>
          <p>{error}</p>
        </div>
      )}

      {data && (
        <Routes>
          <Route
            path="/"
            element={
              <Overview
                overview={data}
                onRemoveMachine={removeMachine}
                onClearJobHistory={clearJobHistory}
              />
            }
          />
          <Route
            path="/jobs/:machineId/:jobId"
            element={
              <JobDetail
                overview={data}
                onClearJobHistory={clearJobHistory}
                onLoadPlanAlerts={api.planNotificationOverride}
                onSavePlanAlerts={api.savePlanNotificationOverride}
                onClearPlanAlerts={api.clearPlanNotificationOverride}
              />
            }
          />
          <Route
            path="*"
            element={
              <div className="empty">
                <h2>Nothing here</h2>
                <p>
                  <Link to="..">Back to the overview</Link>
                </p>
              </div>
            }
          />
        </Routes>
      )}
    </>
  );
}
